/**
 * ==========================================================================
 * Premium Image Hero Slider — Vanilla JS
 * Trade Theme / Online Store 2.0
 *
 * Zero dependencies. One instance per section. Fully cleaned up on
 * Shopify section unload to avoid memory leaks / duplicate listeners.
 * ==========================================================================
 */
(function () {
  'use strict';

  var SELECTOR = '[data-premium-image-slider]';
  var instances = new Map();

  /**
   * Reads a boolean-ish data attribute ("true"/"false" strings from Liquid).
   */
  function readBool(value) {
    return value === true || value === 'true';
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function PremiumImageSlider(section) {
    this.section = section;
    this.viewport = section.querySelector('[data-pis-viewport]');
    this.track = section.querySelector('[data-pis-track]');
    if (!this.track) return; // no slides rendered, nothing to init

    this.slides = Array.prototype.slice.call(section.querySelectorAll('[data-pis-slide]'));
    this.dots = Array.prototype.slice.call(section.querySelectorAll('[data-pis-dot]'));
    this.prevBtn = section.querySelector('[data-pis-prev]');
    this.nextBtn = section.querySelector('[data-pis-next]');

    this.slideCount = this.slides.length;
    if (this.slideCount < 2) {
      // Single slide: nothing to animate, just show it and exit.
      return;
    }

    // Settings pulled from data attributes (rendered server-side by Liquid).
    this.settings = {
      autoplay: readBool(section.dataset.autoplay),
      autoplaySpeed: parseInt(section.dataset.autoplaySpeed, 10) || 5000,
      infinite: readBool(section.dataset.infinite),
      pauseOnHover: readBool(section.dataset.pauseHover),
      pauseOnTabInactive: readBool(section.dataset.pauseTab),
      enableDrag: readBool(section.dataset.drag),
      enableSwipe: readBool(section.dataset.swipe),
      enableKeyboard: readBool(section.dataset.keyboard),
      transition: section.dataset.transition === 'slide' ? 'slide' : 'fade',
      transitionSpeed: parseInt(section.dataset.transitionSpeed, 10) || 500
    };

    this.reducedMotion = prefersReducedMotion();
    if (this.reducedMotion) {
      this.settings.transitionSpeed = 0;
    }

    this.isSlideMode = this.settings.transition === 'slide';
    this.useClones = this.isSlideMode && this.settings.infinite;

    // Default offset/trackSlides for non-clone setups (fade mode, or
    // non-infinite slide mode) — prevents NaN in transform calculations.
    this.offset = 0;
    this.trackSlides = this.slides;

    // Real (logical) index — always maps to an actual, non-cloned slide.
    this.realIndex = 0;
    this.isAnimating = false;
    this.isDragging = false;
    this.autoplayTimer = null;
    this.dragStartX = 0;
    this.dragDeltaX = 0;
    this.pointerId = null;

    this._bindMethods();
    this._setupClones();
    this._bindEvents();
    this._goTo(0, false);
    this._startAutoplay();
  }

  PremiumImageSlider.prototype._bindMethods = function () {
    this.onPointerDown = this._onPointerDown.bind(this);
    this.onPointerMove = this._onPointerMove.bind(this);
    this.onPointerUp = this._onPointerUp.bind(this);
    this.onKeyDown = this._onKeyDown.bind(this);
    this.onMouseEnter = this._pauseAutoplay.bind(this);
    this.onMouseLeave = this._resumeAutoplayIfActive.bind(this);
    this.onVisibilityChange = this._onVisibilityChange.bind(this);
    this.onTransitionEnd = this._onTransitionEnd.bind(this);
  };

  /**
   * For "slide" transition + infinite loop, clone the first/last slide so
   * the track can animate seamlessly past the boundaries. Fade transition
   * does not need clones since it loops via opacity swap.
   */
  PremiumImageSlider.prototype._setupClones = function () {
    if (!this.useClones) return;

    var firstClone = this.slides[0].cloneNode(true);
    var lastClone = this.slides[this.slides.length - 1].cloneNode(true);

    firstClone.setAttribute('data-clone', 'true');
    lastClone.setAttribute('data-clone', 'true');
    firstClone.setAttribute('aria-hidden', 'true');
    lastClone.setAttribute('aria-hidden', 'true');

    // Remove focusable elements inside clones from tab order.
    this._disableFocusable(firstClone);
    this._disableFocusable(lastClone);

    this.track.appendChild(firstClone);
    this.track.insertBefore(lastClone, this.track.firstChild);

    // Track now includes clones: [lastClone, ...slides, firstClone]
    this.trackSlides = Array.prototype.slice.call(this.track.children);
    this.offset = 1; // number of prepended clones
  };

  PremiumImageSlider.prototype._disableFocusable = function (node) {
    var focusables = node.querySelectorAll('[data-pis-focusable]');
    for (var i = 0; i < focusables.length; i++) {
      focusables[i].setAttribute('tabindex', '-1');
    }
  };

  PremiumImageSlider.prototype._bindEvents = function () {
    if (this.prevBtn) this.prevBtn.addEventListener('click', this._handlePrevClick.bind(this));
    if (this.nextBtn) this.nextBtn.addEventListener('click', this._handleNextClick.bind(this));

    this.dots.forEach(function (dot) {
      dot.addEventListener('click', this._handleDotClick.bind(this, dot));
    }, this);

    if (this.settings.enableKeyboard) {
      this.section.addEventListener('keydown', this.onKeyDown);
    }

    if (this.settings.pauseOnHover) {
      this.section.addEventListener('mouseenter', this.onMouseEnter);
      this.section.addEventListener('mouseleave', this.onMouseLeave);
    }

    if (this.settings.pauseOnTabInactive) {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    if (this.settings.enableDrag || this.settings.enableSwipe) {
      this.viewport.addEventListener('pointerdown', this.onPointerDown, { passive: true });
    }

    if (this.isSlideMode) {
      this.track.addEventListener('transitionend', this.onTransitionEnd);
    }
  };

  PremiumImageSlider.prototype._handlePrevClick = function () {
    this._pauseAutoplay();
    this.goToPrev();
    this._resumeAutoplayIfActive();
  };

  PremiumImageSlider.prototype._handleNextClick = function () {
    this._pauseAutoplay();
    this.goToNext();
    this._resumeAutoplayIfActive();
  };

  PremiumImageSlider.prototype._handleDotClick = function (dot) {
    this._pauseAutoplay();
    var index = parseInt(dot.getAttribute('data-index'), 10);
    this._goTo(index, true);
    this._resumeAutoplayIfActive();
  };

  PremiumImageSlider.prototype._onKeyDown = function (event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this._pauseAutoplay();
      this.goToPrev();
      this._resumeAutoplayIfActive();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this._pauseAutoplay();
      this.goToNext();
      this._resumeAutoplayIfActive();
    }
  };

  /* ------------------------------------------------------------------
   * Pointer (mouse drag + touch swipe) handling — unified via Pointer Events
   * ------------------------------------------------------------------ */
  PremiumImageSlider.prototype._onPointerDown = function (event) {
    var isTouch = event.pointerType === 'touch';
    var isMouse = event.pointerType === 'mouse';

    if (isTouch && !this.settings.enableSwipe) return;
    if (isMouse && !this.settings.enableDrag) return;

    this.isDragging = true;
    this.pointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragDeltaX = 0;
    this.section.setAttribute('data-dragging', 'true');
    this._pauseAutoplay();

    this.viewport.addEventListener('pointermove', this.onPointerMove, { passive: true });
    this.viewport.addEventListener('pointerup', this.onPointerUp);
    this.viewport.addEventListener('pointercancel', this.onPointerUp);
  };

  PremiumImageSlider.prototype._onPointerMove = function (event) {
    if (!this.isDragging || event.pointerId !== this.pointerId) return;
    this.dragDeltaX = event.clientX - this.dragStartX;

    if (this.isSlideMode) {
      var percentDelta = (this.dragDeltaX / this.viewport.offsetWidth) * 100;
      var basePercent = -((this.realIndex + this.offset) * 100);
      this.track.style.transform = 'translate3d(' + (basePercent + percentDelta) + '%, 0, 0)';
    }
  };

  PremiumImageSlider.prototype._onPointerUp = function (event) {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.section.removeAttribute('data-dragging');

    this.viewport.removeEventListener('pointermove', this.onPointerMove);
    this.viewport.removeEventListener('pointerup', this.onPointerUp);
    this.viewport.removeEventListener('pointercancel', this.onPointerUp);

    var threshold = this.viewport.offsetWidth * 0.15;

    if (Math.abs(this.dragDeltaX) > threshold) {
      if (this.dragDeltaX < 0) {
        this.goToNext();
      } else {
        this.goToPrev();
      }
    } else if (this.isSlideMode) {
      // Snap back to current slide.
      this._applyTrackPosition(true);
    }

    this.dragDeltaX = 0;
    this._resumeAutoplayIfActive();
  };

  /* ------------------------------------------------------------------
   * Navigation
   * ------------------------------------------------------------------ */
  PremiumImageSlider.prototype.goToNext = function () {
    var next = this.realIndex + 1;
    if (next >= this.slideCount) {
      if (!this.settings.infinite) return;
      next = this.useClones ? this.realIndex + 1 : 0;
    }
    this._goTo(next, true, 'next');
  };

  PremiumImageSlider.prototype.goToPrev = function () {
    var prev = this.realIndex - 1;
    if (prev < 0) {
      if (!this.settings.infinite) return;
      prev = this.useClones ? this.realIndex - 1 : this.slideCount - 1;
    }
    this._goTo(prev, true, 'prev');
  };

  /**
   * Moves to a given logical index.
   * @param {number} index - target real slide index (may be transient during clone animation)
   * @param {boolean} animate
   */
  PremiumImageSlider.prototype._goTo = function (index, animate) {
    if (this.isAnimating && animate) return;

    var targetRealIndex = index;
    if (!this.useClones) {
      // Wrap within bounds for fade mode / non-infinite slide mode.
      targetRealIndex = ((index % this.slideCount) + this.slideCount) % this.slideCount;
    }

    this.pendingRealIndex = targetRealIndex;
    this.isAnimating = animate && !this.reducedMotion;

    if (this.isSlideMode) {
      this._applyTrackPosition(animate, index);
    } else {
      this._applyFade(targetRealIndex);
      this.realIndex = targetRealIndex;
      this._updateUI();
      this.isAnimating = false;
    }
  };

  PremiumImageSlider.prototype._applyTrackPosition = function (animate, rawIndex) {
    var trackIndex = (typeof rawIndex === 'number' ? rawIndex : this.realIndex) + this.offset;
    if (!animate) {
      this.track.style.transition = 'none';
    } else {
      this.track.style.transition = '';
    }
    this.track.style.transform = 'translate3d(' + (-trackIndex * 100) + '%, 0, 0)';

    if (!animate) {
      // Force reflow so the "none" transition is applied before re-enabling.
      // eslint-disable-next-line no-unused-expressions
      this.track.offsetHeight;
      this.track.style.transition = '';
      if (typeof rawIndex === 'number') {
        this.realIndex = ((rawIndex % this.slideCount) + this.slideCount) % this.slideCount;
      }
      this._updateUI();
    }
  };

  PremiumImageSlider.prototype._onTransitionEnd = function (event) {
    if (event.target !== this.track) return;
    if (!this.useClones) {
      this.realIndex = this.pendingRealIndex;
      this._updateUI();
      this.isAnimating = false;
      return;
    }

    // Handle seamless clone wrap-around.
    if (this.pendingRealIndex >= this.slideCount) {
      this.realIndex = 0;
      this._applyTrackPosition(false, 0);
    } else if (this.pendingRealIndex < 0) {
      this.realIndex = this.slideCount - 1;
      this._applyTrackPosition(false, this.slideCount - 1);
    } else {
      this.realIndex = this.pendingRealIndex;
      this._updateUI();
    }
    this.isAnimating = false;
  };

  PremiumImageSlider.prototype._applyFade = function (index) {
    this.slides.forEach(function (slide, i) {
      var active = i === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      var focusable = slide.querySelector('[data-pis-focusable]');
      if (focusable) focusable.setAttribute('tabindex', active ? '0' : '-1');
    });
  };

  PremiumImageSlider.prototype._updateUI = function () {
    // Dots
    this.dots.forEach(function (dot, i) {
      var active = i === this.realIndex;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    }, this);

    // Slide accessibility state (slide mode uses the original, non-cloned nodes)
    if (this.isSlideMode) {
      this.slides.forEach(function (slide, i) {
        var active = i === this.realIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        var focusable = slide.querySelector('[data-pis-focusable]');
        if (focusable) focusable.setAttribute('tabindex', active ? '0' : '-1');
      }, this);
    }

    // Arrow disabled state when not infinite
    if (!this.settings.infinite) {
      if (this.prevBtn) this.prevBtn.disabled = this.realIndex === 0;
      if (this.nextBtn) this.nextBtn.disabled = this.realIndex === this.slideCount - 1;
    }
  };

  /* ------------------------------------------------------------------
   * Autoplay
   * ------------------------------------------------------------------ */
  PremiumImageSlider.prototype._startAutoplay = function () {
    if (!this.settings.autoplay || this.reducedMotion) return;
    this._resumeAutoplayIfActive();
  };

  PremiumImageSlider.prototype._resumeAutoplayIfActive = function () {
    if (!this.settings.autoplay || this.reducedMotion) return;
    if (document.hidden && this.settings.pauseOnTabInactive) return;
    this._pauseAutoplay();
    this.autoplayTimer = window.setInterval(this.goToNext.bind(this), this.settings.autoplaySpeed);
  };

  PremiumImageSlider.prototype._pauseAutoplay = function () {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  };

  PremiumImageSlider.prototype._onVisibilityChange = function () {
    if (document.hidden) {
      this._pauseAutoplay();
    } else {
      this._resumeAutoplayIfActive();
    }
  };

  /* ------------------------------------------------------------------
   * Theme editor support — jump to slide when a block is selected,
   * and pause autoplay while editing to avoid disorientation.
   * ------------------------------------------------------------------ */
  PremiumImageSlider.prototype.onBlockSelect = function (blockId) {
    var slide = this.section.querySelector('[data-pis-slide][data-block-id="' + blockId + '"]');
    var index = this.slides.indexOf(slide);
    this._pauseAutoplay();
    if (index > -1) this._goTo(index, true);
  };

  /**
   * Full teardown: removes every listener this instance attached, and
   * clears any running timers. Called on Shopify section unload/reload.
   */
  PremiumImageSlider.prototype.destroy = function () {
    this._pauseAutoplay();

    if (this.prevBtn) this.prevBtn.removeEventListener('click', this._handlePrevClick);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this._handleNextClick);

    this.section.removeEventListener('keydown', this.onKeyDown);
    this.section.removeEventListener('mouseenter', this.onMouseEnter);
    this.section.removeEventListener('mouseleave', this.onMouseLeave);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    if (this.viewport) {
      this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport.removeEventListener('pointermove', this.onPointerMove);
      this.viewport.removeEventListener('pointerup', this.onPointerUp);
      this.viewport.removeEventListener('pointercancel', this.onPointerUp);
    }

    if (this.track) {
      this.track.removeEventListener('transitionend', this.onTransitionEnd);
    }
  };

  /* ------------------------------------------------------------------
   * Init / lifecycle
   * ------------------------------------------------------------------ */
  function initSection(section) {
    if (instances.has(section)) return;
    var instance = new PremiumImageSlider(section);
    instances.set(section, instance);
  }

  function destroySection(section) {
    var instance = instances.get(section);
    if (instance && typeof instance.destroy === 'function') {
      instance.destroy();
    }
    instances.delete(section);
  }

  function initAll() {
    var sections = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < sections.length; i++) {
      initSection(sections[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /**
   * The section root element is decorated with `data-premium-image-slider`
   * directly (schema "tag": "section"), so `event.target` for section
   * load/unload events IS that element — `closest()` safely matches both
   * that case and any nested-wrapper case.
   */
  function resolveSection(target) {
    if (!target || !target.closest) return null;
    return target.closest(SELECTOR);
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = resolveSection(event.target) || event.target.querySelector(SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var section = resolveSection(event.target) || event.target.querySelector(SELECTOR);
    if (section) destroySection(section);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var section = resolveSection(event.target);
    if (!section) return;
    var instance = instances.get(section);
    if (instance && typeof instance.onBlockSelect === 'function') {
      instance.onBlockSelect(event.detail.blockId);
    }
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var section = resolveSection(event.target);
    if (!section) return;
    var instance = instances.get(section);
    if (instance) instance._resumeAutoplayIfActive();
  });
})();
